import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { agruparPorPedido } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais, type DestinatarioAFaturar } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";

const diaMes = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/** Escolher o que entra no extrato (vem marcado até o fechamento; dá pra tirar ou pôr qualquer
 *  item, como o extrato feito à mão), o mês do extrato e o vencimento — e emitir. */
export function EmitirExtratoDialog({ alvo, onFechar }: { alvo: DestinatarioAFaturar | null; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const consulta = trpc.financeiro.extratos.itensDoDestinatario.useQuery(
    { clientOrganizationId: alvo?.clientOrganizationId ?? "", dentistaId: alvo?.dentistaId ?? null },
    { enabled: !!alvo },
  );
  const dados = consulta.data;
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [mes, setMes] = useState("");
  const [vencimento, setVencimento] = useState("");
  const [enviar, setEnviar] = useState(false);
  useEffect(() => {
    if (!dados) return;
    // Sem preço vem desmarcado, como no "Fechar e enviar": marcado, sairia cobrado a R$ 0,00 por descuido.
    const semPreco = new Set(dados.itens.filter((i) => i.semPreco).map((i) => i.chave));
    setMarcados(new Set(dados.sugestao.selecionados.filter((c) => !semPreco.has(c))));
    setMes(dados.sugestao.rotulo);
    setVencimento(dados.sugestao.vencimento);
    setEnviar(!!dados.email);
  }, [dados]);

  const grupos = useMemo(() => agruparPorPedido(dados?.itens ?? []), [dados]);
  const escolhidos = (dados?.itens ?? []).filter((i) => marcados.has(i.chave));
  const total = escolhidos.reduce((t, i) => t + i.valorCents, 0);
  const alternar = (chaves: string[], ligar: boolean) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      for (const c of chaves) {
        if (ligar) novo.add(c);
        else novo.delete(c);
      }
      return novo;
    });

  const emitir = trpc.financeiro.extratos.emitir.useMutation({
    onSuccess: (e) => {
      utils.financeiro.invalidate();
      toast.success(e.enviadoPara ? `Extrato nº ${e.numero} emitido e enviado para ${e.enviadoPara}.` : `Extrato nº ${e.numero} emitido.`);
      onFechar();
      navigate(`/financeiro/extratos/${e.id}`);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={!!alvo} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="flex max-h-[90dvh] max-w-3xl flex-col gap-4 overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle>Emitir extrato</DialogTitle>
          <DialogDescription>
            {alvo?.nome}
            {alvo?.dentistaId ? ` · ${alvo.clinica}` : ""} — vem marcado o que foi entregue até o fechamento e tem preço; tire ou ponha o que quiser.
          </DialogDescription>
        </DialogHeader>

        {!dados ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {grupos.map((g) => {
                const chaves = g.itens.map((i) => i.chave);
                const todos = chaves.every((c) => marcados.has(c));
                const primeiro = g.itens[0]!;
                return (
                  <div key={g.pedidoId} className="rounded-lg border border-zinc-200 dark:border-border">
                    <label className="flex cursor-pointer items-center gap-2 border-b border-zinc-100 bg-zinc-50/60 px-3 py-2 text-sm dark:border-border dark:bg-muted/30">
                      <input type="checkbox" checked={todos} onChange={(e) => alternar(chaves, e.target.checked)} className="h-4 w-4 accent-brand-600" />
                      <span className="font-semibold text-zinc-900 dark:text-foreground">{primeiro.paciente}</span>
                      {primeiro.pedidoNumero != null && <span className="text-xs text-zinc-500">Pedido {primeiro.pedidoNumero}</span>}
                      {primeiro.dentista && <span className="truncate text-xs text-zinc-500">· {primeiro.dentista}</span>}
                    </label>
                    <ul>
                      {g.itens.map((i) => (
                        <li key={i.chave}>
                          <label className="flex cursor-pointer items-center gap-3 px-3 py-1.5 text-sm hover:bg-zinc-50/60 dark:hover:bg-muted/30">
                            <input type="checkbox" checked={marcados.has(i.chave)} onChange={(e) => alternar([i.chave], e.target.checked)} className="h-4 w-4 accent-brand-600" />
                            <span className="w-8 text-center text-xs font-semibold tabular-nums text-zinc-500">{i.tipo === "SERVICO" ? `${i.quantidade}×` : ""}</span>
                            <span className="min-w-0 flex-1 truncate text-zinc-800 dark:text-foreground">
                              {i.descricao}
                              {i.material && <span className="text-zinc-500"> · {i.material}</span>}
                              {i.semPreco && <span className="font-semibold text-amber-600"> · sem preço</span>}
                            </span>
                            <span className="text-xs tabular-nums text-zinc-500">{diaMes(i.data)}</span>
                            <span className="w-24 text-right font-medium tabular-nums text-zinc-900 dark:text-foreground">{reais(i.valorCents)}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            {dados.itens.some((i) => i.semPreco) && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Item sem preço vem desmarcado e fica em A faturar até ter preço. Marque só se for cortesia: ele sai por R$ 0,00.
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <label>
                <span className={rotulo}>Mês do extrato</span>
                <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} className={campo} />
              </label>
              <label>
                <span className={rotulo}>Vencimento</span>
                <input type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} className={campo} />
              </label>
            </div>
            {dados.email ? (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700 dark:text-foreground">
                <input type="checkbox" checked={enviar} onChange={(e) => setEnviar(e.target.checked)} className="h-4 w-4 accent-brand-600" />
                Enviar por e-mail ao emitir <span className="truncate text-zinc-500">· {dados.email.exibicao}</span>
              </label>
            ) : (
              <p className="text-xs text-zinc-500 dark:text-muted-foreground">Sem e-mail cadastrado para este cliente: o extrato fica só emitido. Dá para copiar o link ou baixar o PDF depois.</p>
            )}

            <div className="sticky -bottom-6 -mx-6 -mb-6 flex items-center gap-3 border-t border-zinc-200 bg-white px-6 py-4 dark:border-border dark:bg-card">
              <p className="text-sm text-zinc-600 dark:text-muted-foreground">
                {escolhidos.length} {escolhidos.length === 1 ? "item" : "itens"} ·{" "}
                <span className="font-bold tabular-nums text-zinc-900 dark:text-foreground">{reais(total)}</span>
              </p>
              <div className="ml-auto flex gap-2">
                <button type="button" onClick={onFechar} className="h-10 rounded-md border border-zinc-200 px-4 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground">
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={escolhidos.length === 0 || !mes || !vencimento || emitir.isPending}
                  onClick={() =>
                    alvo &&
                    emitir.mutate({ clientOrganizationId: alvo.clientOrganizationId, dentistaId: alvo.dentistaId, itens: escolhidos.map((i) => i.chave), rotulo: mes, vencimento, enviarPorEmail: enviar && !!dados.email })
                  }
                  className="inline-flex h-10 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
                >
                  {emitir.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {enviar && dados.email ? "Emitir e enviar" : "Emitir extrato"}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
